import React, { useState, useEffect } from 'react';
import { 
  Smartphone, 
  Wifi, 
  WifiOff, 
  RefreshCw, 
  QrCode,
  CheckCircle,
  XCircle,
  AlertCircle
} from 'lucide-react';
import { whatsappService } from '../services/whatsappService';
import toast from 'react-hot-toast';
import QRCode from 'qrcode.react';

const WhatsAppControl = () => {
  const [status, setStatus] = useState(null);
  const [qrCode, setQrCode] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isGeneratingQR, setIsGeneratingQR] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const [isReconnecting, setIsReconnecting] = useState(false);

  useEffect(() => {
    loadStatus();
    const interval = setInterval(loadStatus, 10000); // Atualizar a cada 10 segundos
    return () => clearInterval(interval);
  }, []);

  const loadStatus = async () => {
    try {
      // Usar o endpoint específico do WhatsApp para status mais preciso
      const whatsappStatus = await whatsappService.getStatus();
      console.log('🔍 Debug - Status do WhatsApp:', whatsappStatus);
      
      // Mapear o status para o formato esperado
      const mappedStatus = {
        status: 'OK',
        whatsappStatus: whatsappStatus.status || 'disconnected',
        clientInfo: whatsappStatus.clientInfo,
        timestamp: new Date().toISOString()
      };
      
      setStatus(mappedStatus);
    } catch (error) {
      console.error('Erro ao carregar status:', error);
      // Fallback para health check
      try {
        const healthData = await whatsappService.healthCheck();
        setStatus(healthData);
      } catch (healthError) {
        console.error('Erro no health check:', healthError);
        setStatus({ status: 'ERROR', whatsappStatus: 'disconnected' });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleGenerateQR = async () => {
    setIsGeneratingQR(true);
    try {
      const response = await whatsappService.generateQR();
      if (response.qrCode) {
        setQrCode(response.qrCode);
        toast.success('QR Code gerado com sucesso!');
      } else {
        toast.error('Erro ao gerar QR Code');
      }
    } catch (error) {
      toast.error('Erro ao gerar QR Code');
      console.error('Erro:', error);
    } finally {
      setIsGeneratingQR(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Tem certeza que deseja desconectar o WhatsApp?')) {
      return;
    }

    setIsDisconnecting(true);
    try {
      await whatsappService.disconnect();
      toast.success('WhatsApp desconectado com sucesso!');
      setQrCode(null);
      
      // Forçar atualização imediata do status
      setTimeout(async () => {
        await loadStatus();
      }, 1000); // Aguardar 1 segundo para o backend processar
      
    } catch (error) {
      toast.error('Erro ao desconectar WhatsApp');
      console.error('Erro:', error);
    } finally {
      setIsDisconnecting(false);
    }
  };

  const handleReconnect = async () => {
    setIsReconnecting(true);
    try {
      await whatsappService.reconnect();
      toast.success('Reconexão iniciada!');
      setQrCode(null);
      await loadStatus();
    } catch (error) {
      toast.error('Erro ao reconectar WhatsApp');
      console.error('Erro:', error);
    } finally {
      setIsReconnecting(false);
    }
  };

  const getStatusIcon = (status) => {
    if (status === 'connected') return <Wifi className="h-8 w-8 text-success-600" />;
    if (status === 'disconnected') return <WifiOff className="h-8 w-8 text-danger-600" />;
    return <AlertCircle className="h-8 w-8 text-gray-400" />;
  };

  const getStatusColor = (status) => {
    if (status === 'connected') return 'text-success-600';
    if (status === 'disconnected') return 'text-danger-600';
    return 'text-gray-500';
  };

  const getStatusText = (status) => {
    if (status === 'connected') return 'Conectado';
    if (status === 'disconnected') return 'Desconectado';
    return 'Desconhecido';
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Controle do WhatsApp</h1>
        <p className="mt-2 text-gray-600">
          Gerencie a conexão e sessões do WhatsApp
        </p>
      </div>

      {/* Status atual */}
      <div className="card">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            {getStatusIcon(status?.whatsappStatus)}
            <div className="ml-4">
              <h3 className="text-lg font-medium text-gray-900">
                Status da Conexão
              </h3>
              <p className={`text-sm font-medium ${getStatusColor(status?.whatsappStatus)}`}>
                {getStatusText(status?.whatsappStatus)}
              </p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-sm text-gray-500">Última verificação</p>
            <p className="text-sm font-medium text-gray-900">
              {status?.timestamp ? new Date(status.timestamp).toLocaleTimeString('pt-BR') : 'N/A'}
            </p>
          </div>
        </div>
      </div>

      {/* Informações do usuário */}
      {status?.clientInfo && (
        <div className="card">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Informações do Usuário</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-500">Nome</p>
              <p className="text-sm font-medium text-gray-900">{status.clientInfo.pushname}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Número</p>
              <p className="text-sm font-medium text-gray-900">{status.clientInfo.wid.user}</p>
            </div>
          </div>
        </div>
      )}

      {/* QR Code */}
      {qrCode && (
        <div className="card">
          <h3 className="text-lg font-medium text-gray-900 mb-4">QR Code para Conexão</h3>
          <div className="flex flex-col items-center space-y-4">
            <div className="bg-white p-4 rounded-lg border">
              <QRCode value={qrCode} size={200} />
            </div>
            <p className="text-sm text-gray-600 text-center">
              Escaneie este QR Code com seu WhatsApp para conectar
            </p>
            <div className="flex space-x-2">
              <button
                onClick={() => setQrCode(null)}
                className="btn-secondary"
              >
                Fechar QR Code
              </button>
              <button
                onClick={handleGenerateQR}
                className="btn-primary"
              >
                Gerar Novo QR Code
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Ações */}
      <div className="card">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Ações</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <button
            onClick={handleGenerateQR}
            disabled={isGeneratingQR || status?.whatsappStatus === 'connected'}
            className="btn-primary flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isGeneratingQR ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
            ) : (
              <QrCode className="h-4 w-4 mr-2" />
            )}
            Gerar QR Code
          </button>

          <button
            onClick={handleDisconnect}
            disabled={isDisconnecting || status?.whatsappStatus === 'disconnected'}
            className="btn-danger flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isDisconnecting ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
            ) : (
              <WifiOff className="h-4 w-4 mr-2" />
            )}
            Desconectar
          </button>

          <button
            onClick={handleReconnect}
            disabled={isReconnecting || status?.whatsappStatus === 'connected'}
            className="btn-secondary flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isReconnecting ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600 mr-2"></div>
            ) : (
              <RefreshCw className="h-4 w-4 mr-2" />
            )}
            Reconectar
          </button>
        </div>
      </div>

      {/* Instruções */}
      <div className="card bg-blue-50 border-blue-200">
        <h3 className="text-lg font-medium text-blue-900 mb-2">Como conectar:</h3>
        <ol className="list-decimal list-inside space-y-1 text-sm text-blue-800">
          <li>Clique em "Gerar QR Code"</li>
          <li>Abra o WhatsApp no seu celular</li>
          <li>Vá em Configurações → Aparelhos conectados → Conectar um aparelho</li>
          <li>Escaneie o QR Code que aparecerá na tela</li>
          <li>Aguarde a confirmação da conexão</li>
        </ol>
      </div>

      {/* Botão de atualizar */}
      <div className="flex justify-center">
        <button
          onClick={loadStatus}
          className="btn-secondary flex items-center"
        >
          <RefreshCw className="h-4 w-4 mr-2" />
          Atualizar Status
        </button>
      </div>
    </div>
  );
};

export default WhatsAppControl;
